import { useEffect, useState } from "react";
import { useSearchParams, Link, useNavigate } from "react-router-dom";
import DashboardLayout from "../components/DashboardLayout";
import BrandDashboardLayout from "../components/BrandDashboardLayout";
import RepresentationCard from "../components/RepresentationCard";
import SocialConnectionsCard from "../components/settings/SocialConnectionsCard";
import { useAuth } from "../context/AuthContext";
import { api, formatApiErrorDetail } from "../lib/api";
import { CheckCircle2, RefreshCw, AlertCircle, FileText, ShieldCheck, Briefcase, History, KeyRound, ArrowDown, Trash2, X } from "lucide-react";

export default function Settings() {
  const { user, refresh, setUser } = useAuth();
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const sessionId = params.get("session_id");
  const [paying, setPaying] = useState(false);
  const [paymentMsg, setPaymentMsg] = useState("");
  const [paymentType, setPaymentType] = useState("");
  const [portalLoading, setPortalLoading] = useState(false);
  const [portalError, setPortalError] = useState("");
  const [planUsage, setPlanUsage] = useState(null);
  const [legalVersions, setLegalVersions] = useState(null);
  const [legalHistory, setLegalHistory] = useState([]);
  const [historyOpen, setHistoryOpen] = useState(false);
  // Password change
  const [pwForm, setPwForm] = useState({ current_password: "", new_password: "", confirm: "" });
  const [pwBusy, setPwBusy] = useState(false);
  const [pwMsg, setPwMsg] = useState({ type: "", text: "" });
  // Downgrade
  const [downgradeOpen, setDowngradeOpen] = useState(false);
  const [downgradeBusy, setDowngradeBusy] = useState(false);
  const [downgradeErr, setDowngradeErr] = useState("");
  // Delete
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleteForm, setDeleteForm] = useState({ password: "", confirm: "" });
  const [deleteBusy, setDeleteBusy] = useState(false);
  const [deleteErr, setDeleteErr] = useState("");

  useEffect(() => { api.get("/plan/limits").then(({data}) => setPlanUsage(data)).catch(() => {}); }, [user?.plan]);
  useEffect(() => {
    api.get("/legal/versions").then(({data}) => setLegalVersions(data)).catch(() => {});
    api.get("/legal/acceptances").then(({data}) => setLegalHistory(data)).catch(() => {});
  }, []);

  const openPortal = async () => {
    setPortalLoading(true); setPortalError("");
    try {
      const { data } = await api.post("/payments/portal", { return_url: window.location.href });
      window.location.href = data.url;
    } catch (e) {
      setPortalError(formatApiErrorDetail(e.response?.data?.detail) || e.message);
      setPortalLoading(false);
    }
  };

  useEffect(() => {
    if (!sessionId) return;
    let cancelled = false;
    setPaying(true);
    setPaymentMsg("Checking payment status…");
    setPaymentType("pending");
    const poll = async (attempt = 0) => {
      if (cancelled) return;
      if (attempt >= 8) {
        setPaymentMsg("Payment status check timed out. If you completed payment, refresh in a moment.");
        setPaymentType("error"); setPaying(false); return;
      }
      try {
        const { data } = await api.get(`/payments/status/${sessionId}`);
        if (data.payment_status === "paid") {
          setPaymentMsg(`Payment successful — you're on the ${data.plan?.toUpperCase()} plan.`);
          setPaymentType("success"); setPaying(false);
          await refresh();
          setTimeout(() => setParams({}), 4000);
          return;
        }
        if (data.status === "expired") {
          setPaymentMsg("Payment session expired."); setPaymentType("error"); setPaying(false); return;
        }
        setTimeout(() => poll(attempt + 1), 2000);
      } catch {
        setTimeout(() => poll(attempt + 1), 2000);
      }
    };
    poll();
    return () => { cancelled = true; };
  // eslint-disable-next-line
  }, [sessionId]);

  const planLabel = (user?.plan || "free").toUpperCase();

  const submitPasswordChange = async (e) => {
    e.preventDefault();
    setPwMsg({ type: "", text: "" });
    if (pwForm.new_password !== pwForm.confirm) {
      setPwMsg({ type: "err", text: "New password and confirmation don't match." });
      return;
    }
    if (pwForm.new_password.length < 8) {
      setPwMsg({ type: "err", text: "New password must be at least 8 characters." });
      return;
    }
    setPwBusy(true);
    try {
      await api.post("/auth/change-password", { current_password: pwForm.current_password, new_password: pwForm.new_password });
      setPwMsg({ type: "ok", text: "Password changed successfully." });
      setPwForm({ current_password: "", new_password: "", confirm: "" });
    } catch (e) {
      setPwMsg({ type: "err", text: formatApiErrorDetail(e.response?.data?.detail) || e.message });
    } finally { setPwBusy(false); }
  };

  const confirmDowngrade = async () => {
    setDowngradeBusy(true); setDowngradeErr("");
    try {
      await api.post("/payments/downgrade-to-free", {});
      await refresh();
      setDowngradeOpen(false);
    } catch (e) {
      setDowngradeErr(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally { setDowngradeBusy(false); }
  };

  const confirmDelete = async () => {
    setDeleteErr("");
    if (deleteForm.confirm !== "DELETE") {
      setDeleteErr('Type DELETE (in caps) to confirm.');
      return;
    }
    setDeleteBusy(true);
    try {
      const { data } = await api.post("/auth/delete-account", { password: deleteForm.password, confirm: "DELETE" });
      if (data?.stripe_cancel_status === "failed") {
        // Non-blocking — user is already logged out. We use sessionStorage so
        // the homepage can show a one-time banner about contacting support.
        try { sessionStorage.setItem("post_delete_stripe_failed", "1"); } catch {}
      }
      setUser(false);
      navigate("/", { replace: true });
    } catch (e) {
      setDeleteErr(formatApiErrorDetail(e.response?.data?.detail) || e.message);
      setDeleteBusy(false);
    }
  };

  // Persona-aware layout. Brand users get the brand sidebar; athletes,
  // agents, clubs (and the default fallback) get the standard one. We keep
  // the switch explicit so adding a dedicated agency layout later is a
  // single-line change.
  const Layout = (() => {
    switch (user?.account_type) {
      case "brand": return BrandDashboardLayout;
      case "agent":
      case "club":
      case "athlete":
      default: return DashboardLayout;
    }
  })();

  return (
    <Layout>
      <div className="p-6 md:p-12">
        <div className="mb-10">
          <div className="label-eyebrow">Account</div>
          <h1 className="font-heading text-4xl md:text-5xl uppercase tracking-tighter mt-2" data-testid="settings-title">Settings</h1>
        </div>

        {paymentMsg && (
          <div className={`surface p-5 mb-6 flex items-center gap-3 ${paymentType === "success" ? "border-volt" : paymentType === "error" ? "border-[#FF3B30]/40" : ""}`} data-testid="payment-banner">
            {paymentType === "success" ? <CheckCircle2 className="w-5 h-5 text-volt" /> :
             paymentType === "error" ? <AlertCircle className="w-5 h-5 text-[#FF3B30]" /> :
             <RefreshCw className="w-5 h-5 animate-spin text-ink-muted" />}
            <div className="text-sm">{paymentMsg}</div>
          </div>
        )}

        <div className="grid md:grid-cols-2 gap-6 max-w-4xl">
          <div className="surface p-6">
            <div className="label-eyebrow mb-3">Profile</div>
            <h2 className="font-heading text-2xl uppercase mb-3">{user?.full_name}</h2>
            <div className="text-sm text-ink-muted">{user?.email}</div>
            <div className="text-xs uppercase tracking-widest text-ink-muted mt-1">{user?.account_type}</div>
            <Link to="/app/profile" className="btn-ghost-line inline-block mt-5">Edit profile</Link>
          </div>

          {user?.account_type === "athlete" && <SocialConnectionsCard />}
          {user?.account_type === "athlete" && <DirectoryOptInCard />}
          {user?.account_type === "athlete" && <RepresentationCard />}
          {(user?.account_type === "athlete" || user?.account_type === "agent" || user?.account_type === "club") && <ContactRequestsInbox />}

          <div className="surface p-6">
            <div className="label-eyebrow mb-3">Billing</div>
            <h2 className="font-heading text-2xl uppercase mb-3" data-testid="current-plan">{planLabel} plan</h2>
            <p className="text-sm text-ink-muted">{planLabel === "FREE" ? "You're on the free plan. Upgrade to unlock unlimited outreach, PDF exports and shareable kits." : "You have full access to PITCHD features."}</p>
            <div className="flex flex-wrap gap-2 mt-5">
              <Link to="/app/pricing" className="btn-volt inline-block" data-testid="settings-upgrade-btn">{planLabel === "FREE" ? "Upgrade plan" : "Change plan"}</Link>
              {planLabel !== "FREE" && (
                <>
                  <button onClick={openPortal} disabled={portalLoading} className="btn-ghost-line inline-flex items-center gap-2" data-testid="settings-portal-btn">
                    {portalLoading ? <RefreshCw className="w-4 h-4 animate-spin" /> : null} Manage subscription
                  </button>
                  <button onClick={() => setDowngradeOpen(true)} className="btn-ghost-line inline-flex items-center gap-2 text-ink-muted hover:text-white" data-testid="settings-downgrade-btn">
                    <ArrowDown className="w-4 h-4" /> Downgrade to free
                  </button>
                </>
              )}
            </div>
            {portalError && <div className="text-xs text-[#FF3B30] mt-3">{portalError}</div>}
          </div>

          <div className="surface p-6 md:col-span-2" data-testid="password-card">
            <div className="label-eyebrow mb-3">Security</div>
            <h2 className="font-heading text-2xl uppercase mb-1 flex items-center gap-2">
              <KeyRound className="w-5 h-5 text-volt" /> Change password
            </h2>
            <p className="text-sm text-ink-muted mb-5">Use at least 8 characters. You'll stay signed in after changing it.</p>
            <form onSubmit={submitPasswordChange} className="grid sm:grid-cols-3 gap-3 max-w-3xl">
              <PwField label="Current password" value={pwForm.current_password}
                onChange={(v) => setPwForm({ ...pwForm, current_password: v })} testid="pw-current" autoComplete="current-password" />
              <PwField label="New password" value={pwForm.new_password}
                onChange={(v) => setPwForm({ ...pwForm, new_password: v })} testid="pw-new" autoComplete="new-password" />
              <PwField label="Confirm new password" value={pwForm.confirm}
                onChange={(v) => setPwForm({ ...pwForm, confirm: v })} testid="pw-confirm" autoComplete="new-password" />
              <div className="sm:col-span-3 flex items-center justify-between gap-3 mt-1">
                <div className="text-xs">
                  {pwMsg.text && (
                    <span className={pwMsg.type === "ok" ? "text-volt" : "text-[#FF3B30]"} data-testid="pw-msg">{pwMsg.text}</span>
                  )}
                </div>
                <button type="submit" disabled={pwBusy || !pwForm.current_password || !pwForm.new_password || !pwForm.confirm}
                  className="btn-volt inline-flex items-center gap-2 disabled:opacity-40" data-testid="pw-submit">
                  {pwBusy ? <RefreshCw className="w-3.5 h-3.5 animate-spin" /> : <KeyRound className="w-3.5 h-3.5" />}
                  {pwBusy ? "Saving…" : "Update password"}
                </button>
              </div>
            </form>
          </div>

          <div className="surface p-6 md:col-span-2" data-testid="plan-usage-card">
            <div className="label-eyebrow mb-3">This month's usage</div>
            <h2 className="font-heading text-2xl uppercase mb-5">Plan limits</h2>
            {!planUsage ? (
              <div className="text-sm text-ink-muted">Loading…</div>
            ) : (
              <div className="grid sm:grid-cols-3 gap-5">
                <UsageBar label="Brands" used={planUsage.usage.brands} limit={planUsage.limits.brands} testid="usage-brands" />
                <UsageBar label="Media kits" used={planUsage.usage.media_kits} limit={planUsage.limits.media_kits} testid="usage-mediakits" />
                <UsageBar label="Outreach this month" used={planUsage.usage.monthly_outreach} limit={planUsage.limits.monthly_outreach} testid="usage-outreach" />
              </div>
            )}
            {planUsage && planLabel === "FREE" && (
              <p className="text-xs text-ink-muted mt-5">Pro unlocks 50 brands, unlimited media kits and unlimited monthly outreach. Agency removes all limits.</p>
            )}
          </div>

          <div className="surface p-6 md:col-span-2" data-testid="legal-panel">
            <div className="label-eyebrow mb-3">Legal &amp; data</div>
            <h2 className="font-heading text-2xl uppercase mb-1">Your legal documents</h2>
            <p className="text-sm text-ink-muted mb-5">
              Review the current versions of our legal documents at any time. Your acceptance history is
              recorded as an audit trail for your records.
            </p>

            <div className="grid sm:grid-cols-3 gap-3 mb-5">
              <LegalDocCard
                Icon={FileText}
                label="Terms of Service"
                path="/terms"
                current={legalVersions?.terms}
                accepted={user?.terms_version}
                testid="legal-card-terms"
              />
              <LegalDocCard
                Icon={ShieldCheck}
                label="Privacy Policy"
                path="/privacy"
                current={legalVersions?.privacy}
                accepted={user?.privacy_version}
                testid="legal-card-privacy"
              />
              <LegalDocCard
                Icon={Briefcase}
                label="Commercial Terms"
                path="/commercial-terms"
                current={legalVersions?.commercial_terms}
                accepted={user?.commercial_terms_version}
                testid="legal-card-commercial"
              />
            </div>

            <button
              onClick={() => setHistoryOpen(o => !o)}
              className="btn-ghost-line text-xs inline-flex items-center gap-2"
              data-testid="legal-history-toggle"
            >
              <History className="w-3.5 h-3.5" />
              {historyOpen ? "Hide" : "Show"} acceptance history ({legalHistory.length})
            </button>

            {historyOpen && (
              <div className="mt-4 border border-line divide-y divide-line" data-testid="legal-history-list">
                {legalHistory.length === 0 && (
                  <div className="p-4 text-xs text-ink-muted">No records yet.</div>
                )}
                {legalHistory.map(row => (
                  <div key={row.id} className="px-4 py-3 flex flex-wrap justify-between items-center gap-2 text-xs">
                    <div className="font-mono text-white">
                      {DOC_LABELS[row.document_type] || row.document_type}
                      <span className="text-ink-muted"> · {row.document_version}</span>
                    </div>
                    <div className="text-ink-muted font-mono">{formatDate(row.accepted_at)}</div>
                  </div>
                ))}
              </div>
            )}

            <p className="text-[10px] text-ink-dim mt-5 leading-relaxed">
              PITCHD is a software platform and is not acting as a sports agent, talent agent, legal
              representative, or exclusive representative. See <Link to="/terms" className="text-volt hover:underline">Terms</Link> for details.
            </p>
          </div>

          <div className="surface p-6 border-[#FF3B30]/30 md:col-span-2">
            <div className="label-eyebrow text-[#FF3B30] mb-3">Danger zone</div>
            <h2 className="font-heading text-2xl uppercase mb-3">Delete account</h2>
            <p className="text-sm text-ink-muted mb-4">This will permanently remove your profile, brands, deals, media kits, outreach history, and cancel any active subscription. This action cannot be undone.</p>
            <button onClick={() => setDeleteOpen(true)} className="btn-ghost-line border-[#FF3B30]/60 text-[#FF3B30] hover:bg-[#FF3B30]/10 inline-flex items-center gap-2" data-testid="settings-delete-btn">
              <Trash2 className="w-4 h-4" /> Delete my account
            </button>
          </div>
        </div>
      </div>

      {/* Downgrade confirmation modal */}
      {downgradeOpen && (
        <Modal onClose={() => !downgradeBusy && setDowngradeOpen(false)} testid="downgrade-modal">
          <div className="label-eyebrow mb-2">Confirm downgrade</div>
          <h3 className="font-heading text-2xl uppercase tracking-tight mb-3">Downgrade to Free plan?</h3>
          <p className="text-sm text-ink-muted mb-4">
            Your active subscription will be canceled and you'll lose Pro features immediately:
            unlimited outreach, unlimited media kits, and higher brand caps. You can re-upgrade any time.
          </p>
          {downgradeErr && <div className="text-xs text-[#FF3B30] mb-3" data-testid="downgrade-err">{downgradeErr}</div>}
          <div className="flex justify-end gap-2">
            <button onClick={() => setDowngradeOpen(false)} disabled={downgradeBusy} className="btn-ghost-line">Cancel</button>
            <button onClick={confirmDowngrade} disabled={downgradeBusy}
              className="btn-volt inline-flex items-center gap-2 disabled:opacity-40" data-testid="downgrade-confirm">
              {downgradeBusy ? <RefreshCw className="w-3.5 h-3.5 animate-spin" /> : <ArrowDown className="w-3.5 h-3.5" />}
              {downgradeBusy ? "Downgrading…" : "Yes, downgrade"}
            </button>
          </div>
        </Modal>
      )}

      {/* Delete account confirmation modal */}
      {deleteOpen && (
        <Modal onClose={() => !deleteBusy && setDeleteOpen(false)} testid="delete-modal">
          <div className="label-eyebrow text-[#FF3B30] mb-2">Permanent action</div>
          <h3 className="font-heading text-2xl uppercase tracking-tight mb-3">Delete your account?</h3>
          <p className="text-sm text-ink-muted mb-4">
            This deletes everything: profile, brands, deals, media kits, outreach history, and any active subscription. <span className="text-white font-semibold">This cannot be undone.</span>
          </p>
          <div className="space-y-3">
            <PwField label="Your password" value={deleteForm.password}
              onChange={(v) => setDeleteForm({ ...deleteForm, password: v })} testid="delete-password" autoComplete="current-password" />
            <div>
              <label className="label-eyebrow block mb-1.5">Type <span className="text-[#FF3B30] font-mono">DELETE</span> to confirm</label>
              <input value={deleteForm.confirm} onChange={(e) => setDeleteForm({ ...deleteForm, confirm: e.target.value })}
                className="w-full bg-bg border border-line focus:border-[#FF3B30] outline-none px-3 py-2.5 text-white font-mono"
                data-testid="delete-confirm-input" autoComplete="off" />
            </div>
          </div>
          {deleteErr && <div className="text-xs text-[#FF3B30] mt-3" data-testid="delete-err">{deleteErr}</div>}
          <div className="flex justify-end gap-2 mt-5">
            <button onClick={() => setDeleteOpen(false)} disabled={deleteBusy} className="btn-ghost-line">Cancel</button>
            <button onClick={confirmDelete} disabled={deleteBusy || !deleteForm.password || deleteForm.confirm !== "DELETE"}
              className="px-5 py-2.5 bg-[#FF3B30] text-white text-xs uppercase tracking-widest font-semibold inline-flex items-center gap-2 disabled:opacity-40 hover:bg-[#E0332A] transition" data-testid="delete-confirm">
              {deleteBusy ? <RefreshCw className="w-3.5 h-3.5 animate-spin" /> : <Trash2 className="w-3.5 h-3.5" />}
              {deleteBusy ? "Deleting…" : "Permanently delete"}
            </button>
          </div>
        </Modal>
      )}
    </Layout>
  );
}

function PwField({ label, value, onChange, testid, autoComplete }) {
  return (
    <div>
      <label className="label-eyebrow block mb-1.5">{label}</label>
      <input type="password" value={value} onChange={(e) => onChange(e.target.value)} autoComplete={autoComplete}
        className="w-full bg-bg border border-line focus:border-volt outline-none px-3 py-2.5 text-white"
        data-testid={testid} />
    </div>
  );
}

function Modal({ children, onClose, testid }) {
  return (
    <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose} data-testid={testid}>
      <div className="surface p-6 max-w-md w-full relative" onClick={(e) => e.stopPropagation()}>
        <button onClick={onClose} className="absolute top-3 right-3 text-ink-muted hover:text-white p-1" data-testid={`${testid}-close`}>
          <X className="w-4 h-4" />
        </button>
        {children}
      </div>
    </div>
  );
}

function UsageBar({ label, used, limit, testid }) {
  const unlimited = limit < 0;
  const pct = unlimited ? 0 : Math.min(100, Math.round((used / Math.max(limit, 1)) * 100));
  const danger = !unlimited && pct >= 100;
  const warn = !unlimited && pct >= 80 && !danger;
  const barColor = danger ? "bg-[#FF3B30]" : warn ? "bg-volt" : "bg-volt";
  return (
    <div data-testid={testid}>
      <div className="flex justify-between items-end mb-2">
        <div className="label-eyebrow">{label}</div>
        <div className="text-xs font-mono text-ink-muted">{used}{unlimited ? " / ∞" : ` / ${limit}`}</div>
      </div>
      <div className="h-1.5 bg-bg border border-line overflow-hidden">
        <div className={`h-full ${barColor} transition-all`} style={{ width: `${unlimited ? 10 : pct}%`, opacity: unlimited ? 0.3 : 1 }} />
      </div>
      {danger && <div className="text-[10px] uppercase tracking-widest text-[#FF3B30] mt-2">Limit reached</div>}
    </div>
  );
}

const DOC_LABELS = {
  terms: "Terms of Service",
  privacy: "Privacy Policy",
  commercial_terms: "Commercial Terms",
};

function formatDate(iso) {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleString(undefined, { year: "numeric", month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" });
  } catch { return iso; }
}

function LegalDocCard({ Icon, label, path, current, accepted, testid }) {
  const status = !accepted
    ? { text: "Not accepted yet", className: "text-ink-muted" }
    : accepted === current
      ? { text: `Accepted · ${accepted}`, className: "text-volt" }
      : { text: `Outdated · ${accepted} → ${current || "?"}`, className: "text-[#FF3B30]" };
  return (
    <div className="border border-line p-4 flex flex-col" data-testid={testid}>
      <div className="flex items-center gap-2 mb-2">
        <Icon className="w-4 h-4 text-volt" />
        <div className="font-heading text-sm uppercase tracking-tight">{label}</div>
      </div>
      <div className={`text-[10px] uppercase tracking-widest mt-1 ${status.className}`} data-testid={`${testid}-status`}>
        {status.text}
      </div>
      <Link
        to={path}
        target="_blank"
        rel="noreferrer"
        className="mt-3 text-xs uppercase tracking-widest text-volt hover:underline self-start"
        data-testid={`${testid}-link`}
      >
        View →
      </Link>
    </div>
  );
}

function DirectoryOptInCard() {
  const [optIn, setOptIn] = useState(false);
  const [budget, setBudget] = useState("");
  const [loaded, setLoaded] = useState(false);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState("");

  useEffect(() => {
    api.get("/profile").then(({ data }) => {
      setOptIn(!!data.is_directory_opt_in);
      setBudget(data.directory_min_budget_gbp ? String(data.directory_min_budget_gbp) : "");
      setLoaded(true);
    }).catch(() => setLoaded(true));
  }, []);

  const save = async (next) => {
    setSaving(true); setMsg("");
    try {
      const body = { is_directory_opt_in: next };
      if (budget && !isNaN(parseInt(budget, 10))) body.directory_min_budget_gbp = parseInt(budget, 10);
      await api.put("/profile/directory", body);
      setOptIn(next);
      setMsg(next ? "You're now visible in the brand directory." : "Removed from the brand directory.");
    } catch (e) {
      setMsg(e.response?.data?.detail || "Failed to update");
    } finally { setSaving(false); }
  };

  return (
    <div className="surface p-6" data-testid="directory-optin-card">
      <div className="label-eyebrow mb-3">Brand Directory</div>
      <h2 className="font-heading text-2xl uppercase mb-1">Get discovered by brands</h2>
      <p className="text-sm text-ink-muted mb-5">When opted-in, brands on PITCHD can see your profile and open partnership conversations with you. Your email and phone are <span className="text-white">never</span> shared — all messages flow through your inbox.</p>
      <div className="flex items-center gap-3 mb-4">
        <button onClick={() => save(!optIn)} disabled={!loaded || saving}
          data-testid="directory-optin-toggle"
          className={`relative w-12 h-7 transition-colors ${optIn ? "bg-volt" : "bg-line"} disabled:opacity-50`}>
          <span className={`absolute top-1 ${optIn ? "left-6" : "left-1"} w-5 h-5 bg-bg transition-all`} />
        </button>
        <span className="text-sm">{optIn ? "Listed in directory" : "Not listed"}</span>
      </div>
      <div className="grid sm:grid-cols-2 gap-3 max-w-md">
        <div>
          <label className="label-eyebrow block mb-2">Minimum partnership budget (£)</label>
          <input type="number" value={budget} onChange={e => setBudget(e.target.value)} placeholder="500"
            data-testid="directory-min-budget-input"
            className="w-full bg-bg border border-line focus:border-volt outline-none px-3 py-2 text-white text-sm" />
        </div>
        <div className="flex items-end">
          <button onClick={() => save(optIn)} disabled={!loaded || saving}
            data-testid="directory-save-budget-btn"
            className="btn-ghost-line text-sm disabled:opacity-40">Save budget</button>
        </div>
      </div>
      {msg && <div className="text-xs text-volt mt-3" data-testid="directory-msg">{msg}</div>}
    </div>
  );
}


function ContactRequestsInbox() {
  const [items, setItems] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const [deciding, setDeciding] = useState(null);

  const load = () => {
    setLoaded(false);
    api.get("/contact-requests/inbox").then(({ data }) => setItems(data)).finally(() => setLoaded(true));
  };
  useEffect(() => { load(); }, []);

  const decide = async (id, decision) => {
    setDeciding(id);
    try {
      await api.post(`/contact-requests/${id}/decision`, { decision });
      load();
    } catch (e) {
      alert(e.response?.data?.detail || "Failed");
    } finally { setDeciding(null); }
  };

  const pending = items.filter(r => r.status === "pending");
  const decided = items.filter(r => r.status !== "pending");

  return (
    <div className="surface p-6" data-testid="contact-requests-card" id="contact-requests">
      <div className="label-eyebrow mb-3">Brand collaboration requests</div>
      <h2 className="font-heading text-2xl uppercase mb-1">Direct collaboration</h2>
      <p className="text-sm text-ink-muted mb-5">Brands on Brand Pro can request a direct collaboration channel. <span className="text-white">You decide.</span> Approving lets that specific brand reach you outside the partnership inbox — your data stays portable.</p>

      {!loaded ? <div className="text-xs text-ink-muted">Loading…</div> : items.length === 0 ? (
        <div className="text-xs text-ink-muted">No requests yet.</div>
      ) : (
        <div className="space-y-3">
          {pending.length > 0 && pending.map(r => (
            <div key={r.id} className="border border-volt/40 bg-bg p-4" data-testid={`contact-req-${r.id}`}>
              <div className="flex justify-between items-start gap-3 flex-wrap">
                <div className="min-w-0 flex-1">
                  <div className="font-heading text-base uppercase">{r.brand_display}</div>
                  <div className="text-[10px] uppercase tracking-widest text-ink-muted">{r.brand_category}</div>
                  <p className="text-sm text-ink mt-2 leading-relaxed whitespace-pre-wrap">{r.reason}</p>
                </div>
                <div className="flex gap-2 shrink-0">
                  <button onClick={() => decide(r.id, "approved")} disabled={deciding === r.id}
                    className="btn-volt text-xs disabled:opacity-40" data-testid={`contact-approve-${r.id}`}>
                    Approve
                  </button>
                  <button onClick={() => decide(r.id, "denied")} disabled={deciding === r.id}
                    className="btn-ghost-line text-xs disabled:opacity-40" data-testid={`contact-deny-${r.id}`}>
                    Deny
                  </button>
                </div>
              </div>
            </div>
          ))}
          {decided.length > 0 && (
            <details className="text-xs text-ink-muted mt-4" data-testid="contact-history">
              <summary className="cursor-pointer label-eyebrow">Past decisions ({decided.length})</summary>
              <div className="mt-3 space-y-2">
                {decided.map(r => (
                  <div key={r.id} className="border border-line p-3 flex justify-between items-center" data-testid={`contact-history-${r.id}`}>
                    <span className="text-white">{r.brand_display}</span>
                    <span className={`text-[10px] uppercase tracking-widest ${r.status === "approved" ? "text-[#34C759]" : "text-[#FF3B30]"}`}>
                      {r.status}
                    </span>
                  </div>
                ))}
              </div>
            </details>
          )}
        </div>
      )}
    </div>
  );
}

